import { Icon } from '@/design-system';
import type { Section } from '@/lib/intake/catalogue';
import { COPY } from './copy';
import styles from './StepNav.module.css';

export const DOCUMENTS_STEP = 9;
export const REVIEW_STEP = 10;

export interface StepNavProps {
  sections: readonly Section[];
  complete: ReadonlySet<string>;
  documentsComplete: boolean;
  current: number;
  onGo: (step: number) => void;
}

/**
 * The stamps: one per section, then documents, then review. Each is a button named
 * by its title, and the current one carries aria-current="step" (SPEC, Non-functional).
 */
export function StepNav({ sections, complete, documentsComplete, current, onGo }: StepNavProps) {
  const stamps = [
    ...sections.map((section, index) => ({
      step: index + 1,
      // The name holds the number the stamp shows, so a person who says what they
      // see to speech control is understood (WCAG 2.5.3).
      title: COPY.stampName(index + 1, section.title),
      done: complete.has(section.id),
      face: String(index + 1),
      icon: null,
    })),
    { step: DOCUMENTS_STEP, title: COPY.documentsTitle, done: documentsComplete, face: '', icon: 'file-text' as const },
    { step: REVIEW_STEP, title: COPY.reviewTitle, done: false, face: '', icon: 'menu' as const },
  ];
  return (
    <nav aria-label={COPY.stamps} className={styles.nav}>
      <ol className={styles.list}>
        {stamps.map((stamp) => {
          const isCurrent = stamp.step === current;
          const state = isCurrent ? styles.current : stamp.done ? styles.done : styles.todo;
          return (
            <li key={stamp.step}>
              <button
                type="button"
                className={[styles.stamp, state].join(' ')}
                onClick={() => onGo(stamp.step)}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={stamp.done ? `${stamp.title}, complete` : stamp.title}
                title={stamp.title}
              >
                {stamp.done && !stamp.icon ? (
                  <Icon name="check" size={16} strokeWidth={3} />
                ) : stamp.icon ? (
                  <Icon name={stamp.icon} size={16} />
                ) : (
                  stamp.face
                )}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
